import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from "@angular/core";
import { ReasoningStepComponent } from "../../../../chat-assistant-message/reasoning-step/reasoning-step.component";
import { BackgroundJobTrackerService } from "../../../../../services/background-job-tracker.service";
import type { BackgroundJobBlockData, BackgroundJobSnapshot } from "../../../../../model/block.types";

/** One row of `analysis_data` in the deep_analysis agent's structured final answer. */
export interface DeepAnalysisDataItem {
  key: string;
  label: string;
  description?: string;
  value: string;
}

/** Structured shape of the deep_analysis agent's final answer, once fully streamed. */
export interface DeepAnalysisResult {
  narrativeSummary: string;
  analysisData: DeepAnalysisDataItem[];
}

/**
 * Full parse — only succeeds once the streamed text is complete, valid JSON matching this
 * shape. Used solely to extract `analysis_data`, which (unlike `narrative_summary`) isn't
 * worth reading incrementally: it's a list of already-atomic rows, so it only makes sense to
 * show once the whole thing has arrived.
 */
export function tryParseDeepAnalysisResult(text: string): DeepAnalysisResult | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.narrative_summary !== "string" && !Array.isArray(obj.analysis_data)) return null;

  const analysisData: DeepAnalysisDataItem[] = Array.isArray(obj.analysis_data)
    ? obj.analysis_data
        .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
        .map((item) => ({
          key: String(item.key ?? ""),
          label: String(item.label ?? item.key ?? ""),
          description: typeof item.description === "string" ? item.description : undefined,
          value: String(item.value ?? ""),
        }))
    : [];

  return { narrativeSummary: typeof obj.narrative_summary === "string" ? obj.narrative_summary : "", analysisData };
}

/**
 * Pulls a top-level string field's value out of a JSON object being streamed as raw text, one
 * JSON escape sequence at a time — so `narrative_summary` can be shown growing, the same way
 * other reasoning messages grow, well before the surrounding object is complete/parseable.
 * Tolerates a dangling escape at the very end of what's arrived so far (mid-flight `\`,
 * `\u00`, ...) by simply omitting it until the next chunk completes it.
 */
export function extractPartialStringField(jsonText: string, fieldName: string): string {
  const marker = `"${fieldName}":"`;
  const start = jsonText.indexOf(marker);
  if (start === -1) return "";

  let result = "";
  let i = start + marker.length;
  while (i < jsonText.length) {
    const ch = jsonText[i];
    if (ch === '"') break; // unescaped closing quote — field complete
    if (ch === "\\") {
      const next = jsonText[i + 1];
      if (next === undefined) break; // dangling escape — rest hasn't arrived yet
      if (next === "u") {
        const hex = jsonText.slice(i + 2, i + 6);
        if (hex.length < 4) break; // \uXXXX not fully arrived yet
        result += String.fromCharCode(parseInt(hex, 16));
        i += 6;
        continue;
      }
      const unescaped: Record<string, string> = { n: "\n", r: "\r", t: "\t", '"': '"', "\\": "\\", "/": "/" };
      result += unescaped[next] ?? next;
      i += 2;
      continue;
    }
    result += ch;
    i++;
  }
  return result;
}

/**
 * Renders the deep_analysis sub-agent's streamed response (deep_analysis_message_chunk
 * CUSTOM events) after the task card instead of nested inside it, since the accumulated text
 * can grow large enough to deform the card. The response is JSON, but reused as-is here: no
 * change made to ReasoningStepComponent (kept generic — it just renders whatever `stepInfo`
 * it's given) — the JSON-field-reading logic lives entirely in this component instead, feeding
 * it the incrementally-extracted `narrative_summary` text as `description`, same visual
 * language as any other reasoning step. `analysis_data` rows are appended once the run
 * finishes and the full JSON parses; raw/crude JSON is never shown, and `generated_artifacts`
 * is never rendered.
 */
@Component({
  selector: "app-deep-analysis-message",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReasoningStepComponent],
  templateUrl: "./deep-analysis-message.component.html",
})
export class DeepAnalysisMessageComponent {
  readonly block = input.required<BackgroundJobBlockData>();

  readonly #tracker = inject(BackgroundJobTrackerService);

  readonly #snapshot = signal<BackgroundJobSnapshot | null>(null);

  protected readonly message = computed<string | null>(() => this.#snapshot()?.deepAnalysis.message || null);

  protected readonly isStreaming = computed<boolean>(() => {
    const status = this.#snapshot()?.run_status;
    return status !== null && status !== "success" && status !== "error" && status !== "timeout";
  });

  /** Full parse, only attempted once the run is done — the source of the final analysisData. */
  readonly #finalResult = computed<DeepAnalysisResult | null>(() => {
    if (this.isStreaming()) return null;
    const text = this.message();
    return text ? tryParseDeepAnalysisResult(text) : null;
  });

  /** Grows progressively while streaming; switches to the cleanly-parsed value once done. */
  protected readonly narrativeText = computed<string>(() => {
    const final = this.#finalResult();
    if (final) return final.narrativeSummary;
    return extractPartialStringField(this.message() ?? "", "narrative_summary");
  });

  protected readonly analysisData = computed<DeepAnalysisDataItem[]>(() => this.#finalResult()?.analysisData ?? []);

  constructor() {
    effect(() => {
      const live = this.#tracker.getSnapshot(this.block().job_id);
      if (live !== null) this.#snapshot.set(live);
    });
  }
}
